import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { IconButton } from "@mui/material";
import { FC, useMemo } from "react";
import { useTranslation } from "react-i18next";
import useEvent from "react-use-event-hook";
import { useRecoilValue } from "recoil";

import {
  DrawerType,
  ICustomField,
  ISelectListItem,
  SKIP_HUB_ID,
  systemSettingsDtoAtom,
  useDrawers,
} from "@ryde/shared";

import { DropdownTemplate } from "../dropdown.template";
import { useHubStationsContext } from "./hub-stations.context";
import {
  HubStationTemplateLoading,
  HubStationTemplateNoHub,
  HubStationTemplateStyled,
  mapHubStationsToSelectItems,
  useAutoSelectDefaultHub,
} from "./parts";

export const HubStationTemplate: FC<ICustomField> = ({
  name,
  formAttributes,
}) => {
  const { t } = useTranslation();
  const { openDrawer } = useDrawers();
  const systemSettings = useRecoilValue(systemSettingsDtoAtom);
  const hubContext = useHubStationsContext();
  const enableHubsLogic = systemSettings?.metadata?.enableHubsLogic;
  const fieldName = name ?? "hubStationId";
  const hasHubStations = Boolean(hubContext?.hubStationsDropdownList?.length);

  useAutoSelectDefaultHub(name);

  const selectItems = useMemo((): ISelectListItem[] => {
    const hubItems = mapHubStationsToSelectItems(
      hubContext?.hubStationsDropdownList,
    );

    return [{ value: SKIP_HUB_ID, label: t("hub.skipHub") }, ...hubItems];
  }, [hubContext?.hubStationsDropdownList, t]);

  const handleOpenHubStationsInfo = useEvent(() => {
    openDrawer({
      drawerProps: {
        type: DrawerType.HubStationsInfo,
      },
    });
  });

  if (!enableHubsLogic || !hubContext) {
    return null;
  }

  if (hubContext.isHubStationsLoading) {
    return (
      <HubStationTemplateStyled>
        <HubStationTemplateLoading />
      </HubStationTemplateStyled>
    );
  }

  return (
    <HubStationTemplateStyled>
      <div className="hub-station-field">
        {hasHubStations ? (
          <DropdownTemplate
            name={fieldName}
            labelTranslated={t("hub.hubStation")}
            formAttributes={{
              ...formAttributes,
              selectItems,
              skipDropdownItemsTranslation: true,
            }}
          />
        ) : (
          <HubStationTemplateNoHub />
        )}
        <IconButton
          className="hub-station-info-button"
          onClick={handleOpenHubStationsInfo}
          aria-label={t("hub.hubStationsInfo")}
          size="small"
        >
          <InfoOutlinedIcon fontSize="small" />
        </IconButton>
      </div>
    </HubStationTemplateStyled>
  );
};
